import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconTypes } from '../components/foundations/Icon';
import css from './CreativeIQLandingPage.css';
import Button from '../components/atoms/Button';
import LandingPageIcon from '../resources/icons/creatives/creative_iq_landing_page.svg';
import API from '../services/api';
import { getUserData } from '../selectors/user';
import toaster from '../utils/toaster';

const FEATURES = [
    {
        iconName: 'chart-treemap',
        titleKey: 'STATIC.CREATIVE_IQ.CREATIVE_GALLERY.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.CREATIVE_GALLERY.DESCRIPTION',
        iconBackgroundColor: 'var(--purple100)',
    },
    {
        iconName: 'tags',
        titleKey: 'STATIC.CREATIVE_IQ.AI_TAGGING.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.AI_TAGGING.DESCRIPTION',
        iconBackgroundColor: 'var(--teal100)',
    },
    {
        iconName: 'table-row',
        titleKey: 'STATIC.CREATIVE_IQ.EXPLORE_REPORT.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.EXPLORE_REPORT.DESCRIPTION',
        iconBackgroundColor: 'var(--blue150)',
    },
    {
        iconName: 'settings-spark',
        titleKey: 'STATIC.CREATIVE_IQ.CUSTOM_DIMENSIONS.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.CUSTOM_DIMENSIONS.DESCRIPTION',
        iconBackgroundColor: 'var(--green100)',
    },
    {
        iconName: 'users-group',
        titleKey: 'STATIC.CREATIVE_IQ.CROSS_TEAM.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.CROSS_TEAM.DESCRIPTION',
        iconBackgroundColor: 'var(--orange100)',
    },
    {
        iconName: 'external-link',
        titleKey: 'STATIC.CREATIVE_IQ.LEARN_MORE.TITLE',
        descriptionKey: 'STATIC.CREATIVE_IQ.LEARN_MORE.DESCRIPTION',
        iconBackgroundColor: 'var(--gray200)',
    },
];

const api = new API();

const URL_CREATIVE_IQ_START_TRIAL = '/api/start_creative_iq_trial';

const hasTrialEnded = trialEndDate => trialEndDate && moment(trialEndDate).diff(new Date(), 'days') < 0;

function CreativeIQLandingPage({ translate }) {
    const navigate = useNavigate();

    const { trials_config: trialsConfig, is_org_admin: isOrgAdmin } = useSelector(state => getUserData(state));

    const [hasStartedTrial, setHasStartedTrial] = React.useState(false);

    const startTrial = () => {
        setHasStartedTrial(true);
        api.getJson(URL_CREATIVE_IQ_START_TRIAL, {
            method: 'POST',
            credentials: 'include',
            cache: false,
            unique: true,
        })
            .then(() => {
                navigate(0);
            })
            .catch(() => {
                toaster.error(translate('STATIC.CREATIVE_IQ.START_TRIAL_ERROR'));
            });
    };

    const trialEndDate = trialsConfig?.creative_iq?.end_date;

    return (
        <div className={css.container}>
            <div className={css.headerSection}>
                <div className={css.leftContent}>
                    <div className={css.title}>
                        <div className={css.featureName}>
                            <Icon name="ai-line" type={IconTypes.MINGCUTE} interactive={false} />
                            <Translate id="STATIC.CREATIVE_IQ.FEATURE_NAME" />
                        </div>
                        <Translate id="STATIC.CREATIVE_IQ.HEADING" />
                    </div>
                    <div className={css.description}>
                        <Translate id="STATIC.CREATIVE_IQ.DESCRIPTION" />
                        {!hasTrialEnded(trialEndDate) && (
                            <div className={css.trialContainer}>
                                <Button
                                    disabled={hasStartedTrial || !isOrgAdmin}
                                    showSpinner={hasStartedTrial}
                                    className={css.trialButton}
                                    onClick={startTrial}
                                >
                                    <Translate
                                        id={`STATIC.CREATIVE_IQ.${
                                            trialEndDate ? 'RESUME_TRIAL_BUTTON' : 'TRY_FREE_BUTTON'
                                        }`}
                                    />
                                </Button>
                                {!isOrgAdmin && <Translate id="STATIC.CREATIVE_IQ.UNAUTHORIZED_TO_START_TRIAL" />}
                            </div>
                        )}
                    </div>
                </div>
                <LandingPageIcon className={css.landingPageIcon} />
            </div>
            <div className={css.featuresSection}>
                <div className={css.featuresHeading}>
                    <Translate id="STATIC.CREATIVE_IQ.WHAT_YOU_WILL_UNLOCK" />
                </div>
                <div className={css.featuresContainer}>
                    {FEATURES.map(feature => (
                        <div className={css.featureCard} key={feature.titleKey}>
                            <Icon
                                name={feature.iconName}
                                interactive={false}
                                className={css.iconWrapper}
                                style={{ backgroundColor: feature.iconBackgroundColor }}
                            />
                            <div className={css.featureContent}>
                                <p className={css.featureTitle}>
                                    <Translate id={feature.titleKey} />
                                </p>
                                <p className={css.featureDescription}>
                                    <Translate id={feature.descriptionKey} />
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

CreativeIQLandingPage.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(CreativeIQLandingPage);
